import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, Calendar, LogOut, User, Clock, ArrowLeft, Home, Stethoscope, CalendarCheck, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import BookingModal from '../components/BookingModal';
import DoctorReviewModal from '../components/DoctorReviewModal';
import DoctorReviews from '../components/DoctorReviews';
import UserAvatar from '../components/UserAvatar';

// Liste des villes disponibles
const CITIES = [
  "Abobo", "Adjamé", "Attécoubé", "Cocody", "Koumassi",
  "Marcory", "Plateau", "Port-Bouet", "Treichville", "Yopougon"
];

// Liste des spécialités
const SPECIALTIES = [
  "Allergologie",
  "Anesthésiologie",
  "Cardiologie",
  "Chirurgie Générale",
  "Chirurgie Orthopédique",
  "Chirurgie Plastique",
  "Dermatologie",
  "Endocrinologie",
  "Gastro-entérologie",
  "Gériatrie",
  "Gynécologie",
  "Hématologie",
  "Infectiologie",
  "Médecine Générale",
  "Médecine Interne",
  "Néphrologie",
  "Neurologie",
  "Oncologie",
  "Ophtalmologie",
  "ORL (Oto-Rhino-Laryngologie)",
  "Pédiatrie",
  "Pneumologie",
  "Psychiatrie",
  "Radiologie",
  "Rhumatologie",
  "Urologie"
];

const PatientDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [searchCity, setSearchCity] = useState('');
  const [searchSpecialty, setSearchSpecialty] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorForReview, setSelectedDoctorForReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    fetchAppointments();
    // Load all doctors on page load
    loadAllDoctors();
  }, []);

  const loadAllDoctors = async () => {
    try {
      const response = await axios.get(`${API}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      console.error('Failed to load doctors', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments', error);
    }
  };

  const searchDoctors = async () => {
    setLoading(true);
    try {
      let url = `${API}/doctors?`;
      if (searchCity && searchCity !== 'all') {
        url += `city=${encodeURIComponent(searchCity)}&`;
      }
      if (searchSpecialty && searchSpecialty !== 'all') {
        url += `specialization=${encodeURIComponent(searchSpecialty)}`;
      }
      const response = await axios.get(url);
      setDoctors(response.data);
      if (response.data.length === 0) {
        toast.info('Aucun médecin trouvé avec ces critères');
      }
    } catch (error) {
      toast.error('Échec de la recherche de médecins');
    } finally {
      setLoading(false);
    }
  };

  const handleBookDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    setShowBookingModal(true);
  };

  const handleBookingSuccess = () => {
    fetchAppointments();
    setShowBookingModal(false);
  };

  const handleReviewDoctor = (doctor) => {
    setSelectedDoctorForReview(doctor);
    setShowReviewModal(true);
  };

  const handleReviewSuccess = () => {
    loadAllDoctors(); // Refresh to get updated ratings
    setShowReviewModal(false);
    setSelectedDoctorForReview(null);
  };

  const cancelAppointment = async (appointmentId) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}/status?status=cancelled_by_patient`);
      toast.success('Rendez-vous annulé');
      fetchAppointments();
    } catch (error) {
      toast.error('Échec de l\'annulation du rendez-vous');
    }
  };

  const handleLogout = () => {
    logout();
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      cancelled_by_doctor: 'bg-red-100 text-red-800',
      cancelled_by_patient: 'bg-orange-100 text-orange-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmé',
      cancelled: 'Annulé',
      cancelled_by_doctor: 'Annulé par le médecin',
      cancelled_by_patient: 'Annulé par vous',
      completed: 'Terminé'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="patient-dashboard">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-1 text-gray-500 hover:text-teal-600 transition-colors"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                <Home className="w-4 h-4" />
              </button>
              <h1 className="text-2xl font-bold text-teal-700">BabiDoc</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                data-testid="profile-link"
              >
                <UserAvatar name={user?.full_name} role={user?.role} size="sm" />
                <span className="font-medium hidden sm:inline">{user?.full_name}</span>
              </button>
              <Button 
                variant="ghost" 
                onClick={handleLogout} 
                data-testid="logout-btn"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5 mr-1" />
                <span className="hidden sm:inline">Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="search" className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="search" data-testid="search-tab">Trouver des Médecins</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="appointments-tab" className="relative">
              Mes Rendez-vous
              {(() => {
                const now = new Date();
                const pendingFutureCount = appointments.filter(apt => 
                  apt.status === 'pending' && new Date(apt.appointment_date) > now
                ).length;
                return pendingFutureCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {pendingFutureCount}
                  </span>
                );
              })()}
            </TabsTrigger>
            <TabsTrigger value="past-appointments" data-testid="past-appointments-tab">
              <Clock className="w-4 h-4 mr-2" />
              Rendez-vous passés
            </TabsTrigger>
          </TabsList>

          <TabsContent value="search">
            {/* Search Form */}
            <Card className="card mb-8">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Search className="w-5 h-5 text-teal-600" />
                  <span>Rechercher des Médecins</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* City Select */}
                  <Select value={searchCity} onValueChange={setSearchCity}>
                    <SelectTrigger className="h-12" data-testid="city-select">
                      <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                      <SelectValue placeholder="Choisir une ville" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les villes</SelectItem>
                      {CITIES.map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Specialty Select */}
                  <Select value={searchSpecialty} onValueChange={setSearchSpecialty}>
                    <SelectTrigger className="h-12" data-testid="specialty-select">
                      <Stethoscope className="w-4 h-4 mr-2 text-gray-400" />
                      <SelectValue placeholder="Choisir une spécialité" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les spécialités</SelectItem>
                      {SPECIALTIES.map((specialty) => (
                        <SelectItem key={specialty} value={specialty}>{specialty}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Search Button */}
                  <Button onClick={searchDoctors} disabled={loading} className="btn-primary h-12" data-testid="search-btn">
                    <Search className="w-5 h-5 mr-2" />
                    {loading ? 'Recherche...' : 'Rechercher'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Doctors List */}
            {doctors.length > 0 ? (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-gray-700">
                  {doctors.length} médecin(s) disponible(s)
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="doctors-list">
                  {doctors.map((doctor) => (
                    <Card key={doctor.id} className="card hover:shadow-lg transition-shadow" data-testid={`doctor-card-${doctor.id}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg mb-1">{doctor.doctor_name || 'Dr.'}</CardTitle>
                            <p className="text-base text-gray-700 font-medium">{doctor.clinic_name}</p>
                            <p className="text-sm text-teal-600 font-medium">{doctor.specialization || 'Médecin'}</p>
                          </div>
                          {doctor.rating > 0 && (
                            <div className="flex items-center space-x-1 badge">
                              <Star className="w-4 h-4 fill-current" />
                              <span>{doctor.rating}</span>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4" />
                            <span>{doctor.city}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4" />
                            <span>{doctor.experience_years} ans d'expérience</span>
                          </div>

                          {/* Biography */}
                          {doctor.bio && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-600 italic line-clamp-3">{doctor.bio}</p>
                            </div>
                          )}
                          
                          {/* Rating and Reviews */}
                          <div className="flex items-center space-x-2">
                            <div className="flex">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`w-4 h-4 ${s <= (doctor.rating || 0) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">
                              {doctor.rating ? `${doctor.rating}/5` : 'Pas encore noté'}
                              {doctor.total_reviews > 0 && ` (${doctor.total_reviews} avis)`}
                            </span>
                          </div>

                          {/* Reviews Section */}
                          <DoctorReviews doctorId={doctor.id} totalReviews={doctor.total_reviews || 0} />

                          <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-semibold text-gray-900">{doctor.consultation_fee?.toLocaleString()} FCFA</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <Button 
                                onClick={() => handleBookDoctor(doctor)} 
                                className="btn-primary"
                                data-testid={`book-${doctor.id}`}
                              >
                                <CalendarCheck className="w-4 h-4 mr-1" />
                                RDV
                              </Button>
                              <Button 
                                variant="outline"
                                onClick={() => handleReviewDoctor(doctor)}
                                className="border-yellow-400 text-yellow-600 hover:bg-yellow-50"
                                data-testid={`review-${doctor.id}`}
                              >
                                <Star className="w-4 h-4 mr-1" />
                                Noter
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <Card className="card">
                <CardContent className="py-12 text-center">
                  <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">Utilisez les filtres ci-dessus pour rechercher des médecins</p>
                  <p className="text-sm text-gray-400">ou cliquez sur "Rechercher" pour voir tous les médecins disponibles</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="appointments">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Mes Rendez-vous à venir</h3>
            {(() => {
              const now = new Date();
              const futureAppointments = appointments.filter(apt => 
                new Date(apt.appointment_date) >= now && 
                !['completed', 'cancelled', 'cancelled_by_doctor', 'cancelled_by_patient'].includes(apt.status)
              );
              return futureAppointments.length === 0 ? (
                <Card className="card">
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucun rendez-vous à venir</p>
                    <p className="text-sm text-gray-400 mt-2">Recherchez un médecin et prenez votre premier rendez-vous !</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="appointments-list">
                  {futureAppointments.map((appointment) => (
                    <Card key={appointment.id} className="card" data-testid={`appointment-${appointment.id}`}>
                      <CardContent className="py-6">
                        <div className="flex justify-between items-start flex-wrap gap-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="text-lg font-semibold">{appointment.doctor_name || 'Dr.'}</h3>
                              {getStatusBadge(appointment.status)}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{appointment.clinic_name}</p>
                            <div className="space-y-1 text-sm text-gray-600">
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4 text-teal-600" />
                                <span>{new Date(appointment.appointment_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4 text-teal-600" />
                                <span>{appointment.time_slot || new Date(appointment.appointment_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                              {appointment.reason && (
                                <p className="mt-2 text-gray-500 italic">Motif: {appointment.reason}</p>
                              )}
                            </div>
                          </div>
                          {appointment.status === 'pending' && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => cancelAppointment(appointment.id)}
                              data-testid={`cancel-${appointment.id}`}
                            >
                              Annuler
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="past-appointments">
            <h3 className="text-lg font-semibold mb-4 text-gray-700">Mes Rendez-vous passés</h3>
            {(() => {
              const now = new Date();
              const pastAppointments = appointments.filter(apt => 
                new Date(apt.appointment_date) < now || 
                ['completed', 'cancelled', 'cancelled_by_doctor', 'cancelled_by_patient'].includes(apt.status)
              ).sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date));
              
              return pastAppointments.length === 0 ? (
                <Card className="card">
                  <CardContent className="py-12 text-center">
                    <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucun rendez-vous passé</p>
                    <p className="text-sm text-gray-400 mt-2">Votre historique de rendez-vous apparaîtra ici</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="past-appointments-list">
                  {pastAppointments.map((appointment) => (
                    <Card key={appointment.id} className="card bg-gray-50" data-testid={`past-appointment-${appointment.id}`}>
                      <CardContent className="py-6">
                        <div className="flex justify-between items-start flex-wrap gap-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="text-lg font-semibold text-gray-700">{appointment.doctor_name || 'Dr.'}</h3>
                              {getStatusBadge(appointment.status)}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{appointment.clinic_name}</p>
                            <div className="space-y-1 text-sm text-gray-600">
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span>{new Date(appointment.appointment_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span>{appointment.time_slot || new Date(appointment.appointment_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                              {appointment.reason && (
                                <p className="mt-2 text-gray-500 italic">Motif: {appointment.reason}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Find the doctor and open booking modal
                              const doctor = doctors.find(d => d.doctor_name === appointment.doctor_name || d.id === appointment.doctor_id);
                              if (doctor) {
                                handleBookDoctor(doctor);
                              } else {
                                toast.info('Recherchez ce médecin pour reprendre un rendez-vous');
                              }
                            }}
                            data-testid={`rebook-${appointment.id}`}
                          >
                            <CalendarCheck className="w-4 h-4 mr-1" />
                            Reprendre RDV
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Modal */}
      {selectedDoctor && (
        <BookingModal
          doctor={selectedDoctor}
          isOpen={showBookingModal}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedDoctor(null);
          }}
          onSuccess={handleBookingSuccess}
        />
      )}

      {/* Review Modal */}
      {selectedDoctorForReview && (
        <DoctorReviewModal
          doctor={selectedDoctorForReview}
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedDoctorForReview(null);
          }}
          onSuccess={handleReviewSuccess}
        />
      )}
    </div>
  );
};

export default PatientDashboard;